/**
 * Role Settings Interfaces
 * Framework-agnostic TypeScript interfaces for role settings functionality
 */

// Dynamic Field Group Interface
export interface RoleSettingField {
  field_name: string;
  field_short_name: string;
  is_selected: number; // 0 or 1
  field_id?: number;
}

export interface RoleSettingFieldGroup {
  group_short_name: string; // e.g., "BNK", "GNRL", "ID"
  [key: string]: RoleSettingField[] | string; // Dynamic property based on group_short_name
}

// Module Interface
export interface RoleSettingModule {
  id: number;
  module_name: string;
  checked: boolean;
  module_order: string | number;
}

// Role Hierarchy Interfaces
export interface RoleSettingChildRole {
  role_id: number;
  value: string; // role name
  is_selected: number; // 0 or 1
}

export interface RoleSettingParentRole {
  role_id: number;
  value: string; // role name
  is_selected: number; // 0 or 1
  sequence: string | number; // Order for approval
}

export interface RoleHierarchy {
  childRoles: RoleSettingChildRole[];
  parentRoles: RoleSettingParentRole[];
}

// Selected Field for Ordering
export interface SelectedField {
  order: number;
  feildName: string; // Note: Keeping typo from legacy code for API compatibility
}

// API Response from GET role configurations
export interface RoleSettingsData {
  // Field groups (dynamic)
  BNK?: { [fieldShortName: string]: string }; // field name -> order
  GNRL?: { [fieldShortName: string]: string };
  ID?: { [fieldShortName: string]: string };

  // Verify flags
  verify_id: number; // 0 or 1
  verify_gst: number;
  verify_aadhar: number;
  verify_bank: number;

  // Enrollment settings
  self_enroll: number; // 0 or 1
  hybrid_enrollment: number; // 0, 1, or 2

  // Can enroll settings
  can_enroll: Array<{
    can_enroll: number; // 0 or 1
    child_roles: number[]; // array of role IDs
  }>;

  // Auto approval settings
  auto_approval: Array<{
    auto_approval: number; // 0 or 1
    parent_roles: number[]; // array of role IDs
    parent_roles_sequence: number[]; // order of approval
  }>;

  // App enable
  app_enable: number; // 0 or 1

  // Modules
  modules: Array<{
    id: number;
    module_name: string;
    module_order: number;
  }>;
}

export interface RoleSettingsApiResponse {
  data: RoleSettingsData;
}

// Update Payload
export interface RoleSettingsUpdatePayload {
  role_id: number;
  field_short_names: string[]; // array of all field group names + special fields

  // Dynamic field groups
  BNK?: { [fieldShortName: string]: string };
  GNRL?: { [fieldShortName: string]: string };
  ID?: { [fieldShortName: string]: string };

  // Verify flags
  verify_id: number;
  verify_gst: number;
  verify_aadhar: number;
  verify_bank: number;

  // Enrollment
  self_enroll: number;
  hybrid_enrollment: number;

  // Can enroll
  can_enroll: number;
  child_roles: number[];

  // Auto approval
  auto_approval: number;
  parent_roles: number[];
  parent_roles_sequence: number[];

  // App enable
  appenable: number; // Note: Different spelling from response (app_enable vs appenable)

  // Modules
  module_id: number[];
  module_value: (string | number)[];
}

// Simple Role Interface (for role name lookup)
export interface RoleInfo {
  role_id: number;
  value: string;
  role_name?: string;
  role_short_name?: string;
}
